<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { PropType, ref } from "vue";
import { DirectLink, Project, Trainer, TranslatedProduct } from "../../../types/PersologOnlineTypes.ts";
import { v4 as uuidv4 } from "uuid";
import { InputNumberInputEvent } from "primevue/inputnumber";
import BuyButton from "../../common/BuyButton.vue";
import { getProductQuantity } from "../../../utils/ProjectUtils";
import { parseServerDate } from "@persolog/utils";
import DirectLinkExpiryInput from "../directlink/DirectLinkExpiryInput.vue";

const { t } = useI18n();

const props = defineProps({
    product: {
        type: Object as PropType<TranslatedProduct>,
        required: true,
    },
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    directLink: {
        type: Object as PropType<DirectLink | null>,
        required: true,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
});

const emit = defineEmits(["directLink", "valid"]);

const directLink = ref<DirectLink | null>(props.directLink ?? null);

// Amount of profiles to be used in direct link
const quantity = ref(directLink?.value?.quantity || 0);

// Optional point in time at which the direct link is deleted automatically (e.g. after a webinar)
const expiresAt = ref<Date | undefined>(
    directLink.value?.expiresAt ? parseServerDate(directLink.value.expiresAt) : undefined,
);

const productQuantity = ref(props.product?.quantity || 0);
const invalidQuantity = ref<boolean>(false);

function ensureDirectLink(): DirectLink {
    if (directLink.value == null) {
        directLink.value = {
            quantity: 0,
            depleted: false,
            hash: uuidv4(),
            project: props.project,
        };
    }

    return directLink.value;
}

const parseQuantity = (event: InputNumberInputEvent) => {
    quantity.value = Math.min(event.value as number, productQuantity.value || 0);
    if (props.project === undefined || props.product === undefined) {
        invalidQuantity.value = true;
        emit("valid", false);
        return;
    }

    invalidQuantity.value = false;
    const updatedDirectLink = ensureDirectLink();
    updatedDirectLink.quantity = quantity.value;
    emit("directLink", updatedDirectLink);
    validate();
};

function updateExpiry(value: Date | null | undefined) {
    const updatedDirectLink = ensureDirectLink();
    // The date picker's clear button yields null, so normalise both "empty" values for the backend.
    updatedDirectLink.expiresAt = value?.toISOString() ?? null;
    emit("directLink", updatedDirectLink);
}

async function updateQuantity() {
    productQuantity.value = await getProductQuantity(props.trainer?.id, props.project?.defaultProduct ?? props.product);
}

function validate() {
    invalidQuantity.value = !directLink.value || invalidQuantity.value === true;
    const valid = !invalidQuantity.value || quantity.value === 0;
    emit("valid", valid);
    return valid;
}
defineExpose({ validate });
</script>

<template>
    <div class="flex flex-wrap gap-4 mt-4">
        <div class="mb-4 flex-1">
            <h3 v-if="props.project">{{ props.project.name }}</h3>
            <h4>{{ t("project-configuration.directLinkExplained") }}</h4>
            <FloatLabel class="mt-4 mb-2">
                <InputNumber
                    show-buttons
                    button-layout="horizontal"
                    mode="decimal"
                    id="direct-link-quantity"
                    :min="0"
                    :max="productQuantity"
                    v-model="quantity"
                    @input="parseQuantity"
                    :class="['w-full', { 'p-invalid': invalidQuantity }]"
                >
                    <template #incrementicon>
                        <span class="pi pi-plus" />
                    </template>
                    <template #decrementicon>
                        <span class="pi pi-minus" />
                    </template>
                </InputNumber>
            </FloatLabel>
            <div class="mt-2 flex items-center">
                <span class="mr-4">
                    <i class="pi pi-exclamation-circle mr-1"></i>
                    {{
                        t(
                            productQuantity === 1
                                ? "project-configuration.directLinkContingentExplainedSingle"
                                : "project-configuration.directLinkContingentExplained",
                            { quantity: productQuantity },
                        )
                    }}
                </span>
                <BuyButton
                    :product="product.productNo"
                    :product-name="product.productName"
                    :trainer="props.trainer"
                    postfix="-dl"
                    @update="updateQuantity"
                />
            </div>
            <DirectLinkExpiryInput
                v-model="expiresAt"
                input-id="configure-direct-link-tab_input-datepicker_expiry"
                class="mt-6"
                @update:model-value="updateExpiry"
            />
        </div>
    </div>
</template>
